import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../App.css";
import { motion, useInView, useSpring, useTransform, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Heart,
  Shield,
  Users,
  CalendarCheck,
  Storefront,
  MapPin,
  ArrowRight,
  AppleLogo,
  GooglePlayLogo,
  InstagramLogo,
  XLogo,
  FacebookLogo,
  TiktokLogo,
  Sparkle,
  Star,
  CheckCircle,
  UserCirclePlus,
  MagnifyingGlass,
  Handshake
} from "@phosphor-icons/react";

// African Flags Pattern Component
const AfricanFlagsBar = () => {
  const flags = [
    // Nigeria
    ["#008751", "#FFFFFF", "#008751"],
    // Ghana
    ["#EF3340", "#FCD116", "#008751"],
    // Kenya
    ["#000000", "#BB1600", "#006B3F"],
    // Ethiopia
    ["#009A49", "#FCDD09", "#DA1219"],
    // South Africa
    ["#007749", "#FFB81C", "#DE3831"],
    // Cameroon
    ["#007A5E", "#CE1126", "#FCD116"],
    // Senegal
    ["#00853F", "#FDEF42", "#E31B23"],
    // Ivory Coast
    ["#FF8200", "#FFFFFF", "#009A44"],
    // Tanzania
    ["#1EB53A", "#FCD116", "#00A3DD"],
    // Uganda
    ["#000000", "#FCDC04", "#D90000"],
  ];

  return (
    <div className="w-full h-2 flex">
      {flags.map((colors, flagIndex) => (
        <div key={flagIndex} className="flex-1 flex">
          {colors.map((color, colorIndex) => (
            <div
              key={colorIndex}
              className="flex-1 h-full"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = "", prefix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const spring = useSpring(0, { mass: 0.8, stiffness: 75, damping: 15 });
  const display = useTransform(spring, (current) =>
    Math.floor(current).toLocaleString()
  );

  useEffect(() => {
    if (isInView) {
      spring.set(value);
    }
  }, [isInView, spring, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      <motion.span>{display}</motion.span>
      {suffix}
    </span>
  );
};

// Scroll Reveal Component
const ScrollReveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Data
const STATS = [
  { value: 3000, suffix: "+", label: "Active Users" },
  { value: 150, suffix: "+", label: "Matches Made" },
  { value: 80, suffix: "+", label: "Success Stories" },
  { value: 10, suffix: "", label: "Provinces" }
];

const FEATURES = [
  {
    icon: Heart,
    title: "Smart Matching",
    description: "Our advanced algorithm connects you based on shared values, cultural background, and genuine compatibility.",
    span: "lg:col-span-8"
  },
  {
    icon: Shield,
    title: "AI Verification",
    description: "Every profile is verified using facial recognition technology to ensure authentic connections.",
    span: "lg:col-span-4"
  },
  {
    icon: Users,
    title: "Communities",
    description: "Join groups based on interests, tribe, or city and build meaningful friendships.",
    span: "lg:col-span-4"
  },
  {
    icon: CalendarCheck,
    title: "Events & Networking",
    description: "Discover local Afrobeat parties, cultural festivals, and exclusive networking events curated for our community.",
    span: "lg:col-span-8"
  },
  {
    icon: Storefront,
    title: "Discover Businesses",
    description: "Support Black-owned businesses. Find restaurants, services, and entrepreneurs in your area.",
    span: "lg:col-span-4"
  },
  {
    icon: MapPin,
    title: "Canada Focused",
    description: "Built specifically for the African diaspora in Canada. Connect with people in Toronto, Vancouver, Montreal, and beyond.",
    span: "lg:col-span-8"
  }
];

const STEPS = [
  {
    icon: UserCirclePlus,
    number: "01",
    title: "Create Your Profile",
    description: "Share your story, interests, and what you're looking for in a partner."
  },
  {
    icon: Shield,
    number: "02",
    title: "Get Verified",
    description: "Complete our quick AI verification to join our trusted community."
  },
  {
    icon: MagnifyingGlass,
    number: "03",
    title: "Discover Matches",
    description: "Browse curated profiles and connect with people who share your values."
  },
  {
    icon: Handshake,
    number: "04",
    title: "Meet IRL",
    description: "Take the connection offline and write your own love story."
  }
];

const TESTIMONIALS = [
  {
    quote: "I found my soulmate through AfroConnect. We bonded over our shared love for jollof rice debates and now we're planning our wedding!",
    name: "Amara & Kwame",
    location: "Toronto, ON",
    image: "https://images.unsplash.com/photo-1599593588412-2ccefe3cdd23?w=400&h=400&fit=crop"
  },
  {
    quote: "Finally, an app that understands our culture. The community events have been amazing for making friends in a new city.",
    name: "Chioma N.",
    location: "Vancouver, BC",
    image: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&h=400&fit=crop"
  },
  {
    quote: "I discovered so many Black-owned businesses through AfroConnect. It's more than just dating - it's a whole community!",
    name: "David O.",
    location: "Calgary, AB",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop"
  },
  {
    quote: "The verification process made me feel safe. I've met the most genuine people through this app.",
    name: "Fatou D.",
    location: "Montreal, QC",
    image: "https://images.unsplash.com/photo-1589156229687-496a31ad1d1f?w=400&h=400&fit=crop"
  }
];

// Hero Slides Data - Matching exact app content
const HERO_SLIDES = [
  {
    title: "Where Africans Meet in Canada",
    subtitle: "A premium experience for the African diaspora. Find meaningful connections rooted in shared culture and values.",
    image: "https://images.unsplash.com/photo-1599593588412-2ccefe3cdd23?w=800&h=1200&fit=crop&q=80"
  },
  {
    title: "Experience Cultural Events",
    subtitle: "From Afrobeat parties to networking mixers, find events that celebrate African culture in Canada.",
    image: "https://images.unsplash.com/photo-1711743658461-38c875d37c8e?w=800&h=1200&fit=crop&q=80"
  },
  {
    title: "Connect with Your Community",
    subtitle: "Celebrate our rich heritage together. Join events, discover businesses, and build lasting friendships.",
    image: "https://images.unsplash.com/photo-1529832588601-c01e066263a8?w=800&h=1200&fit=crop&q=80"
  }
];

// Logo URL
const LOGO_URL = "/logo.jpg";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* African Flags Bar */}
      <div className="fixed top-0 w-full z-[60]">
        <AfricanFlagsBar />
      </div>

      <motion.header
        data-testid="navigation-header"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-2 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#0D0B12]/90 backdrop-blur-xl border-b border-white/5 shadow-2xl"
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <motion.div 
            className="flex items-center gap-3"
            whileHover={{ scale: 1.02 }}
          >
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-lg overflow-hidden">
              <img src={LOGO_URL} alt="AfroConnect" className="w-8 h-8 object-contain" />
            </div>
            <span className="font-serif text-2xl text-white tracking-tight">AfroConnect</span>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {["Features", "How It Works", "Stories"].map((item) => (
              <button
                key={item}
                data-testid={`nav-${item.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => scrollToSection(item.toLowerCase().replace(/\s+/g, "-"))}
                className="text-[#A19B9F] hover:text-white transition-colors duration-300 text-sm tracking-wide"
              >
                {item}
              </button>
            ))}
          </div>

          <Link to="/app/signup" data-testid="nav-join-button" className="hidden md:flex items-center gap-2 bg-[#E07A5F] text-white font-semibold px-6 py-2.5 rounded-full hover:bg-[#C96B52] transition-colors duration-300">
            Get Started
            <ArrowRight weight="bold" className="w-4 h-4" />
          </Link>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span className={`w-full h-0.5 bg-white transform transition-all ${mobileMenuOpen ? "rotate-45 translate-y-2" : ""}`} />
              <span className={`w-full h-0.5 bg-white transition-all ${mobileMenuOpen ? "opacity-0" : ""}`} />
              <span className={`w-full h-0.5 bg-white transform transition-all ${mobileMenuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
            </div>
          </button>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#0D0B12]/95 backdrop-blur-xl border-t border-white/5"
            >
              <div className="px-6 py-6 flex flex-col gap-4">
                {["Features", "How It Works", "Stories"].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase().replace(/\s+/g, "-"))}
                    className="text-[#A19B9F] hover:text-white transition-colors text-left py-2"
                  >
                    {item}
                  </button>
                ))}
                <button className="flex items-center justify-center gap-2 bg-[#E07A5F] text-white font-semibold px-6 py-3 rounded-full mt-2" onClick={() => { window.location.href = '/app/signup'; }}>
                  Get Started
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};

// Hero Section with Carousel
const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const timerRef = useRef(null);

  const startAutoRotation = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 5000);
  };

  useEffect(() => {
    startAutoRotation();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleDotClick = (index) => {
    setCurrentSlide(index);
    startAutoRotation(); // Reset the timer when manually changing slides
  };

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0D0B12 0%, #1a1520 50%, #0D0B12 100%)"
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/4 -right-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#E07A5F]/10 to-transparent blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-1/4 -left-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-[#E07A5F]/10 to-transparent blur-3xl"
        />
      </div>

      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #E07A5F 1px, transparent 0)`,
          backgroundSize: "40px 40px"
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#E07A5F]/30 bg-[#E07A5F]/10 mb-8"
            >
              <Sparkle weight="fill" className="text-[#E07A5F] w-4 h-4" />
              <span className="text-[#E07A5F] text-sm uppercase tracking-wider font-medium">
                #1 African Community App in Canada
              </span>
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-none text-white mb-6">
                  {currentSlide === 0 && (
                    <>
                      Where Africans
                      <br />
                      <span className="text-[#E07A5F]">Meet in Canada</span>
                    </>
                  )}
                  {currentSlide === 1 && (
                    <>
                      Experience
                      <br />
                      <span className="text-[#E07A5F]">Cultural Events</span>
                    </>
                  )}
                  {currentSlide === 2 && (
                    <>
                      Connect with
                      <br />
                      <span className="text-[#E07A5F]">Your Community</span>
                    </>
                  )}
                </h1>

                <p className="text-lg md:text-xl text-[#A19B9F] leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0">
                  {HERO_SLIDES[currentSlide].subtitle}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Carousel Dots */}
            <div className="flex gap-2 mb-8 justify-center lg:justify-start">
              {HERO_SLIDES.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  data-testid={`carousel-dot-${index}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? "w-8 bg-[#E07A5F]" 
                      : "w-2 bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Link
                to="/app/signup"
                data-testid="hero-get-started-button"
                className="bg-[#E07A5F] text-white font-semibold px-8 py-4 rounded-full hover:bg-[#C96B52] transition-all duration-300 flex items-center justify-center gap-2"
              >
                Get Started
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>

              <Link
                to="/app/login"
                data-testid="hero-login-button"
                className="bg-transparent border-2 border-white/20 text-white px-8 py-4 rounded-full hover:bg-white/5 transition-all duration-300 flex items-center justify-center"
              >
                I already have an account
              </Link>
            </motion.div>

            <p className="text-[#686366] text-sm mt-6 text-center lg:text-left">
              By continuing, you agree to our <a href="/terms" className="text-white/70 hover:text-white underline">Terms of Service</a> & <a href="/privacy" className="text-white/70 hover:text-white underline">Privacy Policy</a>
            </p>
          </div>

          {/* Right Content - Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: "easeOut" }}
            className="relative flex justify-center"
          >
            {/* Glow Effect */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="w-80 h-80 rounded-full bg-gradient-to-br from-[#E07A5F]/30 to-[#E07A5F]/10 blur-3xl"
              />
            </div>

            {/* Phone Frame */}
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="relative z-10"
            >
              <div className="w-[280px] md:w-[320px] h-[560px] md:h-[640px] bg-[#1a1a1a] rounded-[3rem] p-2 shadow-2xl border border-white/10 overflow-hidden">
                {/* Phone Screen */}
                <div className="w-full h-full bg-[#0D0B12] rounded-[2.5rem] overflow-hidden relative">
                  {/* Flags Bar Inside Phone */}
                  <div className="w-full h-1.5">
                    <AfricanFlagsBar />
                  </div>
                  
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#1a1a1a] rounded-b-2xl z-20" />
                  
                  {/* Screen Content */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentSlide}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 top-1.5"
                    >
                      <img
                        src={HERO_SLIDES[currentSlide].image}
                        alt="App preview"
                        className="w-full h-full object-cover"
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                      
                      {/* Content inside phone */}
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        {/* Logo */}
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center overflow-hidden">
                            <img src={LOGO_URL} alt="AfroConnect" className="w-6 h-6 object-contain" />
                          </div>
                          <span className="text-white font-medium">AfroConnect</span>
                        </div>
                        
                        <h3 className="font-serif text-2xl text-white mb-2 leading-tight">
                          {HERO_SLIDES[currentSlide].title}
                        </h3>
                        
                        <p className="text-white/70 text-xs mb-4 line-clamp-2">
                          {HERO_SLIDES[currentSlide].subtitle}
                        </p>
                        
                        {/* Dots */}
                        <div className="flex gap-1.5 mb-4">
                          {HERO_SLIDES.map((_, i) => (
                            <div
                              key={i}
                              className={`h-1 rounded-full transition-all ${
                                i === currentSlide ? "w-4 bg-[#E07A5F]" : "w-1 bg-white/30"
                              }`}
                            />
                          ))}
                        </div>
                        
                        {/* Button */}
                        <div className="bg-[#E07A5F] text-white text-center py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2">
                          Get Started
                          <ArrowRight weight="bold" className="w-4 h-4" />
                        </div>
                        
                        <div className="border border-white/20 text-white text-center py-2.5 rounded-full text-sm mt-2">
                          I already have an account
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2"
        >
          <motion.div className="w-1 h-2 bg-[#E07A5F] rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  return (
    <section data-testid="stats-section" className="py-20 bg-[#0D0B12] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {STATS.map((stat, index) => (
            <ScrollReveal key={index} delay={index * 0.1}>
              <div className="text-center">
                <p className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-white mb-2">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07A5F]">
                  {stat.label}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  return (
    <section id="features" data-testid="features-section" className="py-24 md:py-32 lg:py-40 bg-[#0D0B12]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ScrollReveal>
          <div className="text-center mb-16 md:mb-24">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07A5F] mb-4 block">
              Why Choose Us
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-5xl font-light tracking-tight leading-tight text-white mb-6">
              Everything You Need to
              <br />
              <span className="text-[#E07A5F]">Connect & Thrive</span>
            </h2>
            <p className="text-base md:text-lg text-[#A19B9F] leading-relaxed max-w-2xl mx-auto">
              We've built the most comprehensive community platform for the African diaspora in Canada.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {FEATURES.map((feature, index) => (
            <ScrollReveal 
              key={index} 
              delay={index * 0.1}
              className={`col-span-12 ${feature.span}`}
            >
              <motion.div
                whileHover={{ scale: 1.02, borderColor: "rgba(224, 122, 95, 0.3)" }}
                transition={{ duration: 0.3 }}
                className="group bg-[#16121C] border border-white/5 rounded-2xl p-8 md:p-10 relative overflow-hidden h-full shadow-[0_8px_32px_rgba(0,0,0,0.4)] cursor-pointer"
              >
                {/* Hover Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#E07A5F]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-[#E07A5F]/10 flex items-center justify-center mb-6 group-hover:bg-[#E07A5F]/20 transition-colors duration-300">
                    <feature.icon weight="duotone" className="w-7 h-7 text-[#E07A5F]" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-medium text-white mb-3">{feature.title}</h3>
                  <p className="text-[#A19B9F] leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 md:py-32 lg:py-40 bg-[#16121C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ScrollReveal>
          <div className="text-center mb-16 md:mb-24">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07A5F] mb-4 block">
              Getting Started
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-5xl font-light tracking-tight leading-tight text-white mb-6">
              Find Your Match in
              <br />
              <span className="text-[#E07A5F]">4 Simple Steps</span>
            </h2>
          </div>
        </ScrollReveal>

        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#E07A5F]/50 via-[#E07A5F]/20 to-transparent hidden md:block" />

          <div className="space-y-12 md:space-y-0">
            {STEPS.map((step, index) => (
              <ScrollReveal key={index} delay={index * 0.15}>
                <div className={`md:grid md:grid-cols-2 md:gap-20 items-center ${index % 2 === 1 ? "md:flex-row-reverse" : ""}`}>
                  <div className={`mb-8 md:mb-0 ${index % 2 === 1 ? "md:order-2 md:text-left" : "md:text-right"}`}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`inline-block ${index % 2 === 1 ? "" : "md:ml-auto"}`}
                    >
                      <div className="bg-[#1E1826] border border-white/5 rounded-2xl p-8 md:p-10 max-w-md hover:border-[#E07A5F]/20 transition-colors duration-300">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 rounded-full bg-[#E07A5F]/10 flex items-center justify-center">
                            <step.icon weight="duotone" className="w-6 h-6 text-[#E07A5F]" />
                          </div>
                          <span className="font-serif text-3xl text-[#E07A5F]/30">{step.number}</span>
                        </div>
                        <h3 className="text-xl md:text-2xl font-medium text-white mb-3">{step.title}</h3>
                        <p className="text-[#A19B9F] leading-relaxed">{step.description}</p>
                      </div>
                    </motion.div>
                  </div>
                  
                  {/* Center Dot */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#E07A5F] border-4 border-[#16121C]" style={{ top: `${(index * 25) + 5}%` }} />
                  
                  <div className={`hidden md:block ${index % 2 === 1 ? "md:order-1" : ""}`} />
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section id="stories" data-testid="testimonials-section" className="py-24 md:py-32 lg:py-40 bg-[#0D0B12] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16">
        <ScrollReveal>
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07A5F] mb-4 block">
              Success Stories
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-5xl font-light tracking-tight leading-tight text-white mb-6">
              Real People,
              <br />
              <span className="text-[#E07A5F]">Real Connections</span>
            </h2>
          </div>
        </ScrollReveal>
      </div>

      {/* Marquee Testimonials */}
      <Marquee gradient={false} speed={40} pauseOnHover className="py-4">
        {[...TESTIMONIALS, ...TESTIMONIALS].map((testimonial, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02, y: -5 }}
            className="mx-4 w-[350px] md:w-[400px] bg-[#16121C] border border-white/5 rounded-2xl p-8 hover:border-[#E07A5F]/20 transition-all duration-300"
          >
            <div className="flex items-center gap-4 mb-6">
              <img
                src={testimonial.image}
                alt={testimonial.name}
                className="w-14 h-14 rounded-full object-cover border-2 border-[#E07A5F]/30"
              />
              <div>
                <h4 className="text-white font-medium">{testimonial.name}</h4>
                <p className="text-[#A19B9F] text-sm flex items-center gap-1">
                  <MapPin weight="fill" className="w-4 h-4 text-[#E07A5F]" />
                  {testimonial.location}
                </p>
              </div>
            </div>
            <p className="text-[#A19B9F] leading-relaxed italic">"{testimonial.quote}"</p>
            <div className="flex gap-1 mt-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} weight="fill" className="w-4 h-4 text-[#E07A5F]" />
              ))}
            </div>
          </motion.div>
        ))}
      </Marquee>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section data-testid="cta-section" className="py-24 md:py-32 lg:py-40 relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E1826] via-[#16121C] to-[#0D0B12]" />
      
      {/* Coral Glow */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#E07A5F]/20 to-transparent blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
        <ScrollReveal>
          <motion.div
            initial={{ scale: 0.9 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight text-white mb-6">
              Ready to Join
              <br />
              <span className="text-[#E07A5F]">Your Community?</span>
            </h2>
            <p className="text-lg md:text-xl text-[#A19B9F] leading-relaxed mb-10 max-w-2xl mx-auto">
              Join thousands of Africans in Canada who are connecting, building, and thriving on AfroConnect.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/app/signup"
                data-testid="cta-create-account-button"
                className="bg-[#E07A5F] text-white font-semibold px-10 py-5 rounded-full hover:bg-[#C96B52] transition-all duration-300 text-lg flex items-center justify-center gap-2"
              >
                <CheckCircle weight="fill" className="w-6 h-6" />
                Create Free Account
              </Link>
            </div>

            <div className="flex justify-center gap-4 mt-8">
              <motion.button
                data-testid="cta-app-store-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/5 border border-white/10 text-white px-6 py-3 rounded-xl hover:bg-white/10 transition-all duration-300 flex items-center gap-3"
              >
                <AppleLogo weight="fill" className="w-8 h-8" />
                <div className="text-left">
                  <p className="text-xs text-[#A19B9F]">Download on the</p>
                  <p className="font-medium">App Store</p>
                </div>
              </motion.button>

              <motion.button
                data-testid="cta-play-store-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/5 border border-white/10 text-white px-6 py-3 rounded-xl hover:bg-white/10 transition-all duration-300 flex items-center gap-3"
              >
                <GooglePlayLogo weight="fill" className="w-8 h-8" />
                <div className="text-left">
                  <p className="text-xs text-[#A19B9F]">Get it on</p>
                  <p className="font-medium">Google Play</p>
                </div>
              </motion.button>
            </div>
          </motion.div>
        </ScrollReveal>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const footerLinks = {
    Product: [
      { label: "Features", href: "#features" },
      { label: "Stories", href: "#stories" },
      { label: "How It Works", href: "#how-it-works" },
    ],
    Support: [
      { label: "Safety Tips", href: "/safety" },
      { label: "Community Guidelines", href: "/community-guidelines" },
      { label: "Contact Us", href: "mailto:support@afroconnect.ca" },
    ],
    Legal: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Community Guidelines", href: "/community-guidelines" },
    ],
  };

  return (
    <footer data-testid="footer-section" className="bg-[#08060A] relative overflow-hidden">
      {/* Giant Background Text */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
        <h2 className="font-serif text-[15vw] font-bold text-white/[0.02] whitespace-nowrap tracking-tighter">
          AFROCONNECT
        </h2>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-12 mb-16">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden">
                <img src={LOGO_URL} alt="AfroConnect" className="w-8 h-8 object-contain" />
              </div>
              <span className="font-serif text-xl text-white tracking-tight">AfroConnect</span>
            </div>
            <p className="text-[#686366] text-sm leading-relaxed">
              Where Africans Meet in Canada
            </p>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-white font-medium mb-4">{category}</h4>
              <ul className="space-y-3">
                {links.map(({ label, href }) => (
                  <li key={label}>
                    <a
                      href={href}
                      className="text-[#686366] hover:text-[#E07A5F] transition-colors duration-300 text-sm"
                      data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-[#686366] text-sm">
            © 2025 AfroConnect. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {[InstagramLogo, XLogo, FacebookLogo, TiktokLogo].map((Icon, index) => (
              <motion.a
                key={index}
                href="#"
                whileHover={{ scale: 1.1, color: "#E07A5F" }}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-[#686366] hover:bg-white/10 transition-all duration-300"
              >
                <Icon weight="fill" className="w-5 h-5" />
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
export default function Landing() {
  return (
    <div className="bg-[#0D0B12] min-h-screen">
      <Navigation />
      <HeroSection />
      <StatsSection />
      <FeaturesSection />
      <HowItWorksSection />
      <TestimonialsSection />
      <CTASection />
      <Footer />
    </div>
  );
}
